import {
  Box,
  Columns,
  Container,
  Heading,
  Stack,
  Text,
  Visible,
} from '@spaceship-fspl/components';
import {
  boxShadow,
  getColor,
  paddingX,
  paddingY,
  transition,
} from '@spaceship-fspl/styles';
import { TrackingCategory } from '@spaceship-fspl/tracking';
import { Card, PanelSection } from 'components';
import { MobileOptions } from 'components/mobile-options';
import actStateIconImage from 'images/first-home_states_act.svg';
import nswStateIconImage from 'images/first-home_states_nsw.svg';
import ntStateIconImage from 'images/first-home_states_nt.svg';
import qldStateIconImage from 'images/first-home_states_qld.svg';
import saStateIconImage from 'images/first-home_states_sa.svg';
import tasStateIconImage from 'images/first-home_states_tas.svg';
import vicStateIconImage from 'images/first-home_states_vic.svg';
import waStateIconImage from 'images/first-home_states_wa.svg';
import Link from 'next/link';
import React, { useState } from 'react';
import styled from 'styled-components';

interface State {
  state?: string | null;
  title?: string | null;
  description?: React.ReactNode;
}

interface StatesPanelProps {
  title?: string | null;
  tagline?: React.ReactNode;
  states?: Array<State>;
  defaultSelectedState?: string;
}

export const StatesPanel: React.FC<StatesPanelProps> = ({
  title,
  tagline,
  states,
  defaultSelectedState,
}) => {
  const [selectedState, setSelectedState] = useState(
    defaultSelectedState ?? states?.[0]?.state,
  );

  return (
    <TrackingCategory category="States Panel">
      <PanelSection backgroundColor="neutral.015">
        <Stack spaceY="lg">
          <Container>
            <Columns alignX="center">
              <Columns.Column width={{ xs: 1, md: 6 / 8, lg: 17 / 24 }}>
                <Stack spaceY={{ xs: 'sm', lg: 'lg' }}>
                  <Heading
                    component="h2"
                    variant={2}
                    color="neutral.100"
                    align="center"
                  >
                    {title}
                  </Heading>

                  <Text
                    variant={1}
                    color="neutral.080"
                    align="center"
                    component="div"
                  >
                    {tagline}
                  </Text>
                </Stack>
              </Columns.Column>
            </Columns>
          </Container>

          {!!states && states.length > 0 && (
            <>
              <Visible isHidden={{ md: true }} displayValue="block">
                <MobileOptions
                  options={states.map((state) => ({
                    ...state,
                    label: state.state ?? '',
                    value: state.state ?? '',
                  }))}
                  selectedOption={selectedState}
                  onChange={(state) => setSelectedState(state)}
                  variant="light"
                  renderOption={(state) => (
                    <Box paddingTop="sm" paddingBottom="md">
                      <StateCard {...state} />
                    </Box>
                  )}
                />
              </Visible>

              <Visible isHidden={{ xs: true, md: false }} displayValue="block">
                <StyledStatesContainer>
                  {states.map((state) => (
                    <StateCard {...state} key={state.state} />
                  ))}
                </StyledStatesContainer>
              </Visible>
            </>
          )}
        </Stack>
      </PanelSection>
    </TrackingCategory>
  );
};

const StateCard: React.FC<State> = ({ state, title, description }) => {
  let stateIcon;

  switch (state?.toLowerCase()) {
    case 'act':
      stateIcon = actStateIconImage;
      break;
    case 'nsw':
      stateIcon = nswStateIconImage;
      break;
    case 'nt':
      stateIcon = ntStateIconImage;
      break;
    case 'qld':
      stateIcon = qldStateIconImage;
      break;
    case 'sa':
      stateIcon = saStateIconImage;
      break;
    case 'tas':
      stateIcon = tasStateIconImage;
      break;
    case 'vic':
      stateIcon = vicStateIconImage;
      break;
    case 'wa':
      stateIcon = waStateIconImage;
      break;
  }

  return (
    <StyledLink href={`/first-home/${state?.toLowerCase()}`}>
      <Box
        key={state}
        width={{ md: 270 }}
        display={{ md: 'inline-block' }}
        marginX={{ md: 'xs' }}
      >
        <StyledCard
          variant="light"
          paddingX="md"
          paddingTop="xl"
          paddingBottom="lg"
        >
          <Stack spaceY="sm">
            <Stack spaceY="xxxs">
              <Heading
                variant={2}
                component="h3"
                color="neutral.100"
                align="center"
              >
                {title}
              </Heading>

              <Text
                variant={1}
                color="neutral.085"
                component="div"
                align="center"
              >
                {description}
              </Text>
            </Stack>

            <StyledStateIconImage src={stateIcon} />
          </Stack>
        </StyledCard>
      </Box>
    </StyledLink>
  );
};

const StyledStatesContainer = styled.div`
  overflow-x: auto;
  white-space: nowrap;
  ${paddingX({ md: 'sm', xl: 'xxxl' })}
  ${paddingY('lg')}

  ::-webkit-scrollbar {
    height: 10px;
  }

  ::-webkit-scrollbar-track {
    background: ${getColor('neutral.015')};
  }

  ::-webkit-scrollbar-thumb {
    background: ${getColor('neutral.015')};
    border-radius: 10px;
  }

  :hover::-webkit-scrollbar-thumb,
  ::-webkit-scrollbar-thumb:hover {
    background: ${getColor('neutral.050')};
  }
`;

const StyledLink = styled(Link)`
  text-decoration: none;
`;

const StyledCard = styled(Card)`
  ${transition}

  :hover {
    ${boxShadow('md')}
  }
`;

const StyledStateIconImage = styled.img`
  width: 225px;
`;
